import { ROUTE_PATH } from '@consts/appConsts'

/*
 * The record the user has open in the app, as the Assistant's page context: "this case",
 * "this report" then mean that record. Only its kind and its id are sent with a question; the
 * server checks that the user may see the record and names it itself. The label kept here (the
 * tab's title) is for the Context chip only.
 *
 * A list screen is a context too: which list it is and, for a case list, its case type - so a
 * question asked there is not answered with "which case type?". Only those two words are sent,
 * both of a fixed vocabulary; nothing of what the list shows (filters, search text) ever is.
 */

export interface AssistantPageContext {
  /** Record kind - or, for a list, the kind of record it lists - as the server names it. */
  type: string
  /** The record's id; empty for a list. */
  id: string
  /** What the chip shows; never sent to the server. */
  label?: string
  /** Set for a list screen (no record is open); for a case list with its case type. */
  list?: { serviceType?: string }
}

interface RecordKind {
  type: string
  /** The kind in words, for the chip. */
  noun: string
  /** The tool that reads one record of the kind; null for a kind that is vouched for as a mention. */
  toolName: string | null
  routes: { detail: (_id: string, _title: string) => { path: string } }[]
}

/**
 * Detail screens whose record the Assistant can be asked about, from the same route table the
 * citation chips open (citationRoutes.ts). The four case types share the case tools.
 */
const RECORD_KINDS: RecordKind[] = [
  {
    type: 'case',
    noun: 'case',
    toolName: 'get_case_detail',
    routes: [
      ROUTE_PATH.assessment,
      ROUTE_PATH.massTorts,
      ROUTE_PATH.ot,
      ROUTE_PATH.co,
    ],
  },
  {
    type: 'supplementary',
    noun: 'supplementary report',
    toolName: 'get_supplementary_report',
    routes: [ROUTE_PATH.supplementary],
  },
  {
    type: 'file_review',
    noun: 'file review',
    toolName: 'get_file_review',
    routes: [ROUTE_PATH.fileReview],
  },
  {
    type: 'medneg',
    noun: 'MedNeg case',
    toolName: 'get_medneg_case',
    routes: [ROUTE_PATH.medneg],
  },
  {
    type: 'ndis',
    noun: 'NDIS case',
    toolName: 'get_ndis_case',
    routes: [ROUTE_PATH.ndis],
  },
  {
    type: 'funding_transaction',
    noun: 'funding transaction',
    toolName: 'get_funding_transaction',
    routes: [ROUTE_PATH.disbursementFunding],
  },
  {
    type: 'facility_agreement',
    noun: 'facility agreement',
    toolName: 'get_facility_agreement',
    routes: [ROUTE_PATH.fundingAgreement],
  },
  {
    type: 'invoice',
    noun: 'invoice',
    toolName: 'get_invoice_detail',
    routes: [ROUTE_PATH.invoice],
  },
  {
    type: 'specialist',
    noun: 'specialist',
    toolName: null,
    routes: [ROUTE_PATH.specialist],
  },
]

interface ListKind {
  type: string
  /** The case type of a case list; none for the list of another module. */
  serviceType?: string
  /** What the chip says the Assistant will assume; the server answers with the same words. */
  label: string
  /** The tool that searches the list. */
  toolName: string
  route: { list: () => { path: string } }
}

/** List screens the Assistant can be told about (AssistantListKinds in the API). */
const LIST_KINDS: ListKind[] = [
  {
    type: 'case',
    serviceType: 'IME',
    label: 'IME cases list',
    toolName: 'search_cases',
    route: ROUTE_PATH.assessment,
  },
  {
    type: 'case',
    serviceType: 'MT',
    label: 'Mass Torts cases list',
    toolName: 'search_cases',
    route: ROUTE_PATH.massTorts,
  },
  {
    type: 'case',
    serviceType: 'OT',
    label: 'OT cases list',
    toolName: 'search_cases',
    route: ROUTE_PATH.ot,
  },
  {
    type: 'case',
    serviceType: 'CO',
    label: 'CO cases list',
    toolName: 'search_cases',
    route: ROUTE_PATH.co,
  },
  {
    type: 'supplementary',
    label: 'Supplementary reports list',
    toolName: 'search_supplementary_reports',
    route: ROUTE_PATH.supplementary,
  },
  {
    type: 'file_review',
    label: 'File reviews list',
    toolName: 'search_file_reviews',
    route: ROUTE_PATH.fileReview,
  },
  {
    type: 'medneg',
    label: 'MedNeg cases list',
    toolName: 'search_medneg_cases',
    route: ROUTE_PATH.medneg,
  },
  {
    type: 'ndis',
    label: 'NDIS cases list',
    toolName: 'search_ndis_cases',
    route: ROUTE_PATH.ndis,
  },
  {
    type: 'funding_transaction',
    label: 'Funding transactions list',
    toolName: 'search_funding_transactions',
    route: ROUTE_PATH.disbursementFunding,
  },
  {
    type: 'invoice',
    label: 'Invoices list',
    toolName: 'search_invoices',
    route: ROUTE_PATH.invoice,
  },
  {
    type: 'specialist',
    label: 'Specialists list',
    toolName: 'search_specialists',
    route: ROUTE_PATH.specialist,
  },
]

const LIST_PATHS: { path: string; kind: ListKind }[] = LIST_KINDS.map(kind => ({
  path: kind.route.list().path,
  kind,
}))

const listKindOf = (type: string, serviceType?: string) =>
  LIST_KINDS.find(
    kind =>
      kind.type === type && (kind.serviceType ?? '') === (serviceType ?? '')
  )

const listContext = (kind: ListKind): AssistantPageContext => ({
  type: kind.type,
  id: '',
  label: kind.label,
  list: kind.serviceType ? { serviceType: kind.serviceType } : {},
})

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const GUID_LENGTH = 36

/** The longest label kept from a link; the chip cuts what is longer. */
const MAX_LABEL_LENGTH = 80

/** "/view/assessments/detail/" → the kind of record that screen shows. */
const DETAIL_PREFIXES: { prefix: string; type: string }[] =
  RECORD_KINDS.flatMap(kind =>
    kind.routes.map(route => ({
      prefix: route.detail('', '').path,
      type: kind.type,
    }))
  )

const kindOf = (type: string) => RECORD_KINDS.find(kind => kind.type === type)

/** True for a kind and an id the server could accept; anything else is never sent. */
const isWellFormed = (type: string, id: string) =>
  kindOf(type) !== undefined && GUID.test(id)

const cleanLabel = (label?: string | null) => {
  const text = (label ?? '').replace(/\s+/g, ' ').trim()
  return text ? text.slice(0, MAX_LABEL_LENGTH) : undefined
}

/**
 * What a path shows: the record of a detail screen of a kind the Assistant knows, or one of the
 * list screens it knows (the list itself: its query string - the filters - is never read). A
 * create screen, any other screen or an id that is not one gives null. The label of a list is
 * the app's own name for it, never the tab's title.
 */
export const pageContextFromPath = (
  path: string | null | undefined,
  label?: string | null
): AssistantPageContext | null => {
  if (!path) {
    return null
  }
  for (const { prefix, type } of DETAIL_PREFIXES) {
    if (!path.startsWith(prefix)) {
      continue
    }
    const id = path.slice(prefix.length, prefix.length + GUID_LENGTH)
    const rest = path.slice(prefix.length + GUID_LENGTH)
    if (GUID.test(id) && (rest === '' || /^[/?#]/.test(rest))) {
      return { type, id: id.toLowerCase(), label: cleanLabel(label) }
    }
  }
  for (const { path: listPath, kind } of LIST_PATHS) {
    if (!path.startsWith(listPath)) {
      continue
    }
    // The list itself: nothing after its path but a trailing slash, a query or a fragment.
    if (/^\/?([?#].*)?$/.test(path.slice(listPath.length))) {
      return listContext(kind)
    }
  }
  return null
}

/** Stable identity of a context: the same record (or list) on another tab is the same context. */
export const pageContextKey = (context: AssistantPageContext | null) => {
  if (!context) {
    return null
  }
  return context.list
    ? `list:${context.type}:${context.list.serviceType ?? ''}`
    : `${context.type}:${context.id}`
}

/** The tool that searches the list a context names; null when the context is not a known list. */
export const pageContextListTool = (context: AssistantPageContext) =>
  context.list
    ? listKindOf(context.type, context.list.serviceType)?.toolName ?? null
    : null

/**
 * The command that searches one case type ("mt-search"): the server offers it only while that
 * case type is on for the user, which is how the app knows before asking.
 */
export const caseListCommand = (serviceType: string) =>
  `${serviceType.toLowerCase()}-search`

/** The kind in words ("supplementary report"), for the chip of a record without a label. */
export const pageContextNoun = (type: string) => kindOf(type)?.noun ?? 'record'

/** The tool that answers about one record of the kind; null when the kind has none. */
export const pageContextTool = (type: string) => kindOf(type)?.toolName ?? null

const PARAMS = {
  type: 'contextType',
  id: 'contextId',
  list: 'contextList',
  serviceType: 'contextServiceType',
} as const

/**
 * The context as query parameters of the standalone page ("Open in new window"): the kind and
 * the id only - for a list, its kind and its case type. The label does not travel in a link
 * (see pageContextFromQuery).
 */
export const pageContextToQuery = (
  context: AssistantPageContext | null
): Record<string, string> => {
  if (context?.list) {
    const kind = listKindOf(context.type, context.list.serviceType)
    if (!kind) {
      return {}
    }
    return {
      [PARAMS.list]: kind.type,
      ...(kind.serviceType ? { [PARAMS.serviceType]: kind.serviceType } : {}),
    }
  }
  if (!context || !isWellFormed(context.type, context.id)) {
    return {}
  }
  return {
    [PARAMS.type]: context.type,
    [PARAMS.id]: context.id,
  }
}

/**
 * The context the standalone page was opened with. The link is the user's own, but it can be
 * edited or sent to someone else: a kind the app does not know or an id that is not one is
 * ignored, and no wording is taken from the link at all. The chip says "This case" / "This
 * report" until the server has vouched for the record and named it (the `context` event); a
 * label in the link would put words of the sender's choosing on a record the server may not
 * even open for this user.
 */
export const pageContextFromQuery = (
  search: string
): AssistantPageContext | null => {
  const params = new URLSearchParams(search)
  const type = params.get(PARAMS.type) ?? ''
  const id = params.get(PARAMS.id) ?? ''
  if (isWellFormed(type, id)) {
    return { type, id: id.toLowerCase() }
  }
  // A list: both words must be the app's own, so the name on the chip is the app's too.
  const list = params.get(PARAMS.list)
  if (!type && !id && list) {
    const kind = listKindOf(list, params.get(PARAMS.serviceType) ?? undefined)
    return kind ? listContext(kind) : null
  }
  return null
}
